'use client';
import { useThrottleFn } from 'ahooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ONE_DAY, TASK_PROGRESS_STORAGE_KEY } from '@/app/constants';
import { getStorageKey, getStorageValue, getStorageValueWithExpiry, safeStorage } from '@/app/utils/client';

// 数据过期时间，单位为毫秒
const DATA_EXPIRE_TIME = ONE_DAY * 2;
// 模拟进度条的更新间隔，单位为毫秒
const UPDATE_INTERVAL = 500;
// 进度条完成时的数值
const FULL_PROGRESS = 100;

interface UseProgressOptions {
  // 存储在 localStorage 中的 id
  id: string;
  // 是否完成，如果完成，进度条会直接到 100%
  hasFinished?: boolean;
  // 预计总等待时间，单位为秒
  allWaitTime: number;
  // 进度条到多少时停止，数值范围为 0-100，默认为 100，即进度条数值走到 100 时停止
  stopProgress?: number;
}

interface UseProgressReturn {
  // 当前进度值 (0-100)
  progress: number;
  // 当前已经走过的时间，单位为秒
  currentTime: number;
  // 取消动画
  cancelAnimation: () => void;
  // 清除进度存储
  clearStorage: () => void;
}

export const removeProgressStorage = (id: string) => {
  safeStorage.removeItem(getStorageKey(TASK_PROGRESS_STORAGE_KEY, id));
};

export const useTaskProgress = ({
  id,
  hasFinished,
  allWaitTime,
  stopProgress = FULL_PROGRESS,
}: UseProgressOptions): UseProgressReturn => {
  const key = useMemo(() => getStorageKey(TASK_PROGRESS_STORAGE_KEY, id), [id]);
  // 进度条当前已经走过的时间，单位为秒
  const [currentTime, setCurrentTime] = useState(0);
  const intervalId = useRef<number | undefined>(undefined);

  // 用于和 currentTime 比较，单位为秒
  const stopTime = useMemo(() => {
    // 限制 stopProgress 的范围在 0-100 之间
    const limitedStopProgress = Math.min(FULL_PROGRESS, Math.max(0, stopProgress));
    return allWaitTime * (limitedStopProgress / FULL_PROGRESS);
  }, [allWaitTime, stopProgress]);

  // 计算当前进度值
  const progress = useMemo(() => {
    if (hasFinished) {
      return FULL_PROGRESS;
    }
    return Math.round((currentTime / allWaitTime) * FULL_PROGRESS);
  }, [allWaitTime, currentTime, hasFinished]);

  const setItem = useCallback(
    (timeLeft: number) => {
      const data = getStorageValue(key);
      const expiresAt = data?.expiresAt ? Number(data.expiresAt) : Date.now() + DATA_EXPIRE_TIME;
      safeStorage.setItem(key, getStorageValueWithExpiry(timeLeft, expiresAt));
    },
    [key],
  );

  const { run: throttledSaveProgress } = useThrottleFn(setItem, { wait: 2000 });

  // 取消动画
  const cancelAnimation = useCallback(() => {
    if (intervalId.current) {
      clearInterval(intervalId.current);
      intervalId.current = undefined;
    }
  }, []);

  // 清除进度存储
  const clearStorage = useCallback(() => {
    safeStorage.removeItem(key);
  }, []);

  useEffect(() => {
    // 如果已经完成，直接返回
    if (hasFinished) {
      cancelAnimation();
      clearStorage();
      return;
    }

    // 从本地取出保存的时间，确保刷新页面时进度条不会重置
    const data = getStorageValue(key);
    if (data?.value) {
      setCurrentTime(Number(data.value));
    }

    // 开始模拟进度条
    intervalId.current = window.setInterval(() => {
      setCurrentTime((prev) => {
        let next = prev + UPDATE_INTERVAL / 1000;

        // 等待时间到了，停止模拟
        if (next >= stopTime) {
          cancelAnimation();
          next = stopTime;
        }
        throttledSaveProgress(next);
        return next;
      });
    }, UPDATE_INTERVAL);

    return () => {
      cancelAnimation();
    };
  }, [hasFinished, stopTime, throttledSaveProgress, cancelAnimation, clearStorage, key]);

  return {
    progress,
    currentTime,
    cancelAnimation,
    clearStorage,
  };
};
